import { useState } from "react";
import { Check, ChevronRight, Sparkles, X } from "lucide-react";
import { Dialog } from "radix-ui";
import { useCheckout } from "#/api/billing";
import type { CheckoutInterval } from "@myapp/shared";
import { LogoMark } from "#/components/LogoMark";

function PlanCard({
  title,
  price,
  subtitle,
  selected,
  onSelect,
}: {
  title: string;
  price: string;
  subtitle: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      className={`w-full rounded-2xl border p-5 text-left transition-all ${
        selected
          ? "border-primary/70 bg-primary/10"
          : "border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.06]"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="mt-1 font-serif text-2xl text-primary">{price}</p>
          <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
        </div>
        <div
          className={`mt-1 h-5 w-5 flex-shrink-0 rounded-full border-2 ${
            selected ? "border-primary bg-primary" : "border-white/30"
          }`}
        />
      </div>
    </button>
  );
}

export function UpgradeModal({
  children,
}: {
  children: React.ReactNode;
}) {
  const checkout = useCheckout();
  const [interval, setInterval] = useState<CheckoutInterval>("yearly");

  const benefits = [
    "No weekly wait — start sessions anytime",
    "Up to 50 sessions per day",
    "Build your personal belief map over time",
    "Revisit past breakthroughs anytime",
  ];

  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-70 bg-black/60 backdrop-blur-sm transition-opacity data-[state=open]:opacity-100 data-[state=closed]:opacity-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-70 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 transition-all data-[state=open]:opacity-100 data-[state=closed]:opacity-0 data-[state=open]:scale-100 data-[state=closed]:scale-95">
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-popover p-6 shadow-[0_0_40px_rgba(0,0,0,0.4)]">
            <Dialog.Close asChild>
              <button
                className="absolute right-4 top-4 rounded-full p-1 text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>

            <div className="mb-4 flex flex-col items-center">
              <LogoMark size="lg" />
              <p className="mt-4 text-xs uppercase tracking-widest text-primary/80">
                Go Premium
              </p>
              <h2 className="mt-2 text-center font-serif text-2xl font-light text-foreground">
                Choose your plan
              </h2>
            </div>

            <div className="mb-6 space-y-2">
              {benefits.map((benefit) => (
                <div
                  key={benefit}
                  className="flex items-start gap-3 text-sm text-foreground/90"
                >
                  <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            <div className="mb-6 space-y-3">
              <PlanCard
                title="Yearly"
                price="$49.99 / year"
                subtitle="Save 48% — about $4.17/month"
                selected={interval === "yearly"}
                onSelect={() => setInterval("yearly")}
              />
              <PlanCard
                title="Monthly"
                price="$7.99 / month"
                subtitle="Cancel anytime"
                selected={interval === "monthly"}
                onSelect={() => setInterval("monthly")}
              />
            </div>

            <button
              onClick={() => checkout.mutate(interval)}
              disabled={checkout.isPending}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-white/10 px-6 py-3 text-sm font-medium text-primary backdrop-blur-sm transition-all hover:bg-white/[0.14] hover:shadow-[0_0_24px_rgba(180,160,220,0.2)] disabled:opacity-40 disabled:hover:shadow-none"
            >
              <Sparkles className="h-4 w-4" />
              {checkout.isPending ? "Opening checkout..." : "Go Premium"}
              <ChevronRight className="h-4 w-4" />
            </button>

            {checkout.isError && (
              <p className="mt-4 max-w-sm text-center text-xs text-destructive-foreground">
                {checkout.error.message}
              </p>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
